import React, { useContext, useState } from 'react';

import { Formik, Field } from 'formik';
import { Row, Col } from 'antd';
import * as Yup from 'yup';

import styled from 'styled-components';

import { EventSubTypeColorsList } from '@constants/SwatchColors';

import { UserContext } from '@contexts/UserContext';

import { getFolderColor, getFolderColorPayload } from '@libs/NwFolders';

import { useUpdateFolder } from '@hooks/API/UseFolders';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import FolderContactsList from '@components/ContactsFolders/FolderContactsList';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWInput, NWPrivacyLevelSelector, NWSwatchPicker, NWTextAreaCharCounter } from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';

//piu' respiro tra i campi che nel form standard (.nw-form-item-container non ha gia' un suo
//margin-bottom: qui si aggiunge soltanto, non si sovrascrive nulla di NwFormWrapper)
const FieldsBlock = styled.div`
    .nw-form-item-container {
        margin-bottom: 1rem;
    }
`;

const SaveErrorText = styled.div`
    color: ${(props) => props.theme.DangerColor};
    font-size: 0.85rem;
    margin-top: -0.5rem;
`;

const validationSchema = Yup.object().shape({
    Name: Yup.string().required('Name is required'),
});

/**
 * Drawer unico per rename e contenuto di una folder esistente: niente Save/Cancel, ogni campo
 * si salva da solo (blur per i testi, change per swatch e privacy), coerente con i contatti sotto
 * che gia' si aggiungono/tolgono/riordinano uno alla volta senza un submit.
 */
const FolderManageDrawer = ({ folder, onClose, zIndex }) => {
    const [saveError, setSaveError] = useState(null);
    useFormFieldFocus('FolderManageName');

    const { currentUser } = useContext(UserContext);
    const isMine = folder.UserCreated === currentUser.UserID;

    const { mutateAsync: updateFolder } = useUpdateFolder();

    const initialValues = {
        Name: folder.Name,
        Description: folder.Description || '',
        Color: getFolderColor(folder.Color),
        PrivacyLevel: folder.PrivacyLevel,
    };

    //patch e' l'oggetto valori gia' aggiornato col campo appena cambiato: chi chiama lo compone
    //perche' su onAfterChange lo stato di Formik non e' ancora quello nuovo (setFieldValue e' async)
    const persistFolder = async (patch) => {
        const name = patch.Name ? patch.Name.trim() : '';
        if (!name) {
            return; //Name obbligatorio: resta l'errore a video, niente PUT finche' non e' valido
        }
        setSaveError(null);
        try {
            await updateFolder({
                folderID: folder.ID,
                folder: {
                    Name: name,
                    Description: patch.Description,
                    Color: getFolderColorPayload(patch.Color),
                    PrivacyLevel: patch.PrivacyLevel,
                },
            });
        } catch (error) {
            console.error('Update Folder Error => ', error);
            setSaveError('Could not save changes');
        }
    };

    return (
        <NwDrawer onClose={onClose} zIndex={zIndex} title="Edit folder" width="40vw">
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={() => {}}>
                {({ values }) => (
                    <NwForm values={values} layout="vertical">
                        <FieldsBlock>
                            <Row gutter={16}>
                                <Col flex="auto">
                                    <Field
                                        component={NWInput}
                                        label="Name"
                                        name="Name"
                                        type="text"
                                        required
                                        id="FolderManageName"
                                        value={values.Name}
                                        onAfterBlur={() => persistFolder(values)}
                                    />
                                </Col>
                                <Col flex="none">
                                    <Field
                                        component={NWSwatchPicker}
                                        label="Color"
                                        name="Color"
                                        required
                                        compact
                                        placement="left"
                                        colorsList={EventSubTypeColorsList}
                                        defaultColor={values.Color}
                                        value={values.Color}
                                        onAfterChange={(newColor) => persistFolder({ ...values, Color: newColor })}
                                    />
                                </Col>
                            </Row>
                            <Field
                                component={NWTextAreaCharCounter}
                                label="Description"
                                name="Description"
                                type="text"
                                maxLength={500}
                                autoSize={{ minRows: 3, maxRows: 3 }}
                                value={values.Description}
                                onAfterBlur={() => persistFolder(values)}
                            />
                            {isMine && (
                                <Field
                                    component={NWPrivacyLevelSelector}
                                    label="Privacy Level"
                                    name="PrivacyLevel"
                                    hasDepartment
                                    hasNetworkAgency
                                    value={values.PrivacyLevel}
                                    onAfterChange={(newPrivacy) =>
                                        persistFolder({ ...values, PrivacyLevel: newPrivacy })
                                    }
                                />
                            )}
                            {saveError && <SaveErrorText>{saveError}</SaveErrorText>}
                        </FieldsBlock>
                    </NwForm>
                )}
            </Formik>
            <FolderContactsList />
        </NwDrawer>
    );
};

export default FolderManageDrawer;
